
import { useCallback, useRef } from 'react';
import axios from 'axios';
import { extractUserFromToken } from '../utils/tokenUtils';
import { User } from '../utils/types';
import { applyWebsiteScopeHeaders } from '../lib/websiteScope';

const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT || "/api";
const ACCESS_TOKEN_NAME = import.meta.env.VITE_AUTH_TOKEN_NAME || 'access_token';
const REFRESH_TOKEN_NAME = import.meta.env.VITE_REFRESH_TOKEN_NAME || 'refresh_token';
const SILENT_REFRESH_BUFFER = 60_000; // 1 minute

interface UseTokenRefreshProps {
  api: any;
  setUser: (user: User | null) => void;
  setIsAuthenticated: (value: boolean) => void;
  logout: () => void;
}

export const useTokenRefresh = ({
  api,
  setUser,
  setIsAuthenticated,
  logout,
}: UseTokenRefreshProps) => {
  const refreshInFlight = useRef<Promise<string> | null>(null);
  const refreshTimer = useRef<number | null>(null);

  const persistTokens = (access: string, refresh?: string) => {
    localStorage.setItem(ACCESS_TOKEN_NAME, access);
    if (refresh) localStorage.setItem(REFRESH_TOKEN_NAME, refresh);
  };

  const scheduleSilentRefresh = useCallback(
    (accessToken: string) => {
      if (refreshTimer.current) {
        window.clearTimeout(refreshTimer.current);
      }

      let delay = 0;
      try {
        const { exp } = extractUserFromToken(accessToken);
        if (typeof exp === 'number') {
          const expiryMs = exp * 1000 - Date.now();
          delay = Math.max(0, expiryMs - SILENT_REFRESH_BUFFER);
        }
      } catch {
        delay = 0;
      }

      refreshTimer.current = window.setTimeout(() => {
        const refresh = localStorage.getItem(REFRESH_TOKEN_NAME);
        if (refresh) {
          refreshAccessToken(refresh).catch(() => {
            // If refresh fails we will log the user out inside refreshAccessToken
          });
        }
      }, delay);
    },
    [api]
  );

  const refreshAccessToken = useCallback(
    async (refreshToken: string): Promise<string> => {
      if (refreshInFlight.current) {
        return refreshInFlight.current;
      }

      refreshInFlight.current = (async () => {
        try {
          const response = await axios.post(
            `${API_BASE_URL}/token/refresh/`,
            { refresh: refreshToken },
            { headers: applyWebsiteScopeHeaders({ 'Content-Type': 'application/json' }) }
          );
          const { access: newAccess, refresh: newRefresh } = response.data;

          persistTokens(newAccess, newRefresh);
          api.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;

          setIsAuthenticated(true);
          const newUser = extractUserFromToken(newAccess);
          setUser(newUser);

          scheduleSilentRefresh(newAccess);

          return newAccess;
        } catch (err) {
          logout();
          throw err;
        } finally {
          refreshInFlight.current = null;
        }
      })();

      return refreshInFlight.current;
    },
    [api, scheduleSilentRefresh, setIsAuthenticated, setUser, logout]
  );

  const clearRefreshTimer = () => {
    if (refreshTimer.current) {
      window.clearTimeout(refreshTimer.current);
    }
  };

  return {
    refreshAccessToken,
    scheduleSilentRefresh,
    clearRefreshTimer,
  };
};
